import React, { useState, useEffect } from 'react';
import { isWhatsAppVerificationEnabled } from '../utils/featureFlags';
import '../styles/UserProfile.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const UserProfile = () => {
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    organization: '',
    designation: ''
  });

  // Feature flag for WhatsApp verification
  const [whatsappVerificationEnabled, setWhatsappVerificationEnabled] = useState(false);

  // WhatsApp OTP verification states
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [otpSuccess, setOtpSuccess] = useState('');
  const [phoneChanged, setPhoneChanged] = useState(false);

  // Load feature flags on component mount
  useEffect(() => {
    const loadFeatureFlags = async () => {
      const enabled = await isWhatsAppVerificationEnabled();
      setWhatsappVerificationEnabled(enabled);
    };
    loadFeatureFlags();
  }, []);

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      const userData = localStorage.getItem('userAuth');
      if (!userData) {
        setMessage({ type: 'error', text: 'Please log in to view your profile' });
        setLoading(false);
        return;
      }

      const userObj = JSON.parse(userData);
      setUser(userObj);
      setFormData({
        name: userObj.name || '',
        email: userObj.email || '',
        phone: userObj.phone || '',
        organization: userObj.organization || '',
        designation: userObj.designation || ''
      });

      // Fetch subscription and usage data
      const userId = userObj.id || userObj.RowKey; // Support both formats
      await Promise.all([
        fetchSubscription(userId),
        fetchUsage(userId)
      ]);

      setLoading(false);
    } catch (err) {
      console.error('Error loading user data:', err);
      setMessage({ type: 'error', text: 'Failed to load profile data' });
      setLoading(false);
    }
  };

  const fetchSubscription = async (userId) => {
    try {
      const response = await fetch(`${API_URL}/user/subscription?userId=${userId}`);
      const data = await response.json();
      if (data.success && data.subscription) {
        setSubscription(data.subscription);
      }
    } catch (err) {
      console.error('Error fetching subscription:', err);
    }
  };

  const fetchUsage = async (userId) => {
    try {
      const response = await fetch(`${API_URL}/user/usage?userId=${userId}`);
      const data = await response.json();
      if (data.success && data.usage) {
        setUsage(data.usage);
      }
    } catch (err) {
      console.error('Error fetching usage:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Track if phone number changed
    if (name === 'phone' && value !== user?.phone) {
      setPhoneChanged(true);
      setOtpSent(false);
      setOtpVerified(false);
      setOtpCode('');
      setOtpError('');
      setOtpSuccess('');
    } else if (name === 'phone' && value === user?.phone) {
      setPhoneChanged(false);
      setOtpSent(false);
      setOtpVerified(false);
    }
  };

  const handleSendOTP = async () => {
    setOtpError('');
    setOtpSuccess('');

    if (!formData.phone) {
      setOtpError('Please enter your WhatsApp number');
      return;
    }

    const phoneRegex = /^\+?[1-9]\d{1,14}$/;
    if (!phoneRegex.test(formData.phone.replace(/[\s-]/g, ''))) {
      setOtpError('Please enter a valid phone number with country code (e.g., +91XXXXXXXXXX)');
      return;
    }

    setOtpLoading(true);

    try {
      const response = await fetch(`${API_URL}/user/send-whatsapp-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phoneNumber: formData.phone.replace(/[\s-]/g, '')
        }),
      });

      const result = await response.json();

      if (result.success) {
        setOtpSent(true);
        setOtpSuccess('OTP sent to your WhatsApp! Please check your messages.');
      } else {
        setOtpError(result.message || 'Failed to send OTP');
      }
    } catch (error) {
      setOtpError('An error occurred. Please try again.');
      console.error('Send OTP error:', error);
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    setOtpError('');
    setOtpSuccess('');

    if (!otpCode || otpCode.length !== 6) {
      setOtpError('Please enter the 6-digit OTP code');
      return;
    }

    setOtpLoading(true);

    try {
      const response = await fetch(`${API_URL}/user/verify-whatsapp-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phoneNumber: formData.phone.replace(/[\s-]/g, ''),
          otpCode: otpCode
        }),
      });

      const result = await response.json();

      if (result.success) {
        setOtpVerified(true);
        setOtpSuccess('WhatsApp number verified successfully! ✓');
      } else {
        setOtpError(result.message || 'Invalid OTP code');
      }
    } catch (error) {
      setOtpError('An error occurred. Please try again.');
      console.error('Verify OTP error:', error);
    } finally {
      setOtpLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    // Check if phone changed but not verified (only if feature is enabled)
    if (whatsappVerificationEnabled && phoneChanged && !otpVerified) {
      setMessage({ type: 'error', text: 'Please verify your WhatsApp number before saving changes' });
      setSaving(false);
      return;
    }

    try {
      // Update user profile via API
      const response = await fetch(`${API_URL}/users/update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userId: user.id || user.RowKey,
          ...formData
        })
      });

      const data = await response.json();

      if (data.success) {
        // Update localStorage with whatsappVerified status
        const updatedUser = {
          ...user,
          ...formData,
          whatsappVerified: phoneChanged ? otpVerified : user.whatsappVerified
        };
        localStorage.setItem('userAuth', JSON.stringify(updatedUser));
        setUser(updatedUser);
        setIsEditing(false);
        setMessage({ type: 'success', text: 'Profile updated successfully!' });

        // Reset OTP states
        setPhoneChanged(false);
        setOtpSent(false);
        setOtpVerified(false);
        setOtpCode('');
        setOtpError('');
        setOtpSuccess('');
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to update profile' });
      }
    } catch (err) {
      console.error('Error updating profile:', err);
      setMessage({ type: 'error', text: 'Failed to update profile. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    // Reset form data to original user data
    setFormData({
      name: user.name || '',
      email: user.email || '',
      phone: user.phone || '',
      organization: user.organization || '',
      designation: user.designation || ''
    });
    setIsEditing(false);
    setMessage({ type: '', text: '' });

    // Reset OTP states
    setPhoneChanged(false);
    setOtpSent(false);
    setOtpVerified(false);
    setOtpCode('');
    setOtpError('');
    setOtpSuccess('');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getSubscriptionStatus = () => {
    if (!subscription) return { text: 'No Active Subscription', class: 'status-none' };

    const statusMap = {
      active: { text: 'Active', class: 'status-active' },
      pending: { text: 'Pending Payment', class: 'status-pending' },
      expired: { text: 'Expired', class: 'status-expired' },
      cancelled: { text: 'Cancelled', class: 'status-cancelled' }
    };

    return statusMap[subscription.status] || { text: subscription.status, class: 'status-unknown' };
  };

  if (loading) {
    return (
      <div className="user-profile-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="user-profile-container">
        <div className="error-message">
          <i className="fas fa-exclamation-circle"></i>
          <p>Please log in to view your profile</p>
          <button onClick={() => window.location.href = '/login'}>Go to Login</button>
        </div>
      </div>
    );
  }

  const statusInfo = getSubscriptionStatus();

  return (
    <div className="user-profile-container">
      <div className="profile-header">
        <div
          className="profile-avatar"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#667eea',
            color: 'white',
            fontSize: '32px',
            fontWeight: 'bold',
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            textTransform: 'uppercase'
          }}
        >
          {(user.name || user.email || '?')
            .trim()
            .split(/\s+/)
            .map(part => part[0])
            .slice(0, 2)
            .join('')}
        </div>
        <div className="profile-header-info">
          <h1>{user.name}</h1>
          <p className="user-email">{user.email}</p>
          <div className={`subscription-status ${statusInfo.class}`}>
            <i className="fas fa-circle"></i>
            <span>{statusInfo.text}</span>
          </div>
        </div>
      </div>

      {message.text && (
        <div className={`message-banner ${message.type}`}>
          <i className={`fas fa-${message.type === 'success' ? 'check-circle' : 'exclamation-circle'}`}></i>
          <span>{message.text}</span>
        </div>
      )}

      <div className="profile-content">
        <div className="profile-main">
          {/* Personal Information Card */}
          <div className="profile-card">
            <div className="card-header">
              <h2>
                <i className="fas fa-user"></i>
                Personal Information
              </h2>
              {!isEditing && (
                <button className="btn-edit" onClick={() => setIsEditing(true)}>
                  <i className="fas fa-edit"></i>
                  Edit Profile
                </button>
              )}
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Full Name</label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                    />
                  ) : (
                    <p className="form-value">{user.name || 'Not provided'}</p>
                  )}
                </div>

                <div className="form-group">
                  <label>Email Address</label>
                  {isEditing ? (
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                  ) : (
                    <p className="form-value">{user.email || 'Not provided'}</p>
                  )}
                </div>

                <div className="form-group">
                  <label>Phone Number</label>
                  {isEditing ? (
                    <div>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="+91XXXXXXXXXX"
                      />
                      <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                        Include country code (e.g., +91 for India)
                      </p>

                      {/* Success message */}
                      {otpSuccess && (
                        <div style={{
                          backgroundColor: '#d4edda',
                          color: '#155724',
                          padding: '10px',
                          borderRadius: '5px',
                          marginTop: '10px',
                          fontSize: '13px'
                        }}>
                          {otpSuccess}
                        </div>
                      )}

                      {/* Error message */}
                      {otpError && (
                        <div style={{
                          backgroundColor: '#f8d7da',
                          color: '#721c24',
                          padding: '10px',
                          borderRadius: '5px',
                          marginTop: '10px',
                          fontSize: '13px'
                        }}>
                          {otpError}
                        </div>
                      )}

                      {/* Show verify button only if phone changed and feature is enabled */}
                      {whatsappVerificationEnabled && phoneChanged && !otpSent && !otpVerified && (
                        <button
                          type="button"
                          onClick={handleSendOTP}
                          disabled={otpLoading || !formData.phone}
                          style={{
                            width: '100%',
                            padding: '10px',
                            backgroundColor: otpLoading ? '#95a5a6' : '#3498db',
                            color: 'white',
                            border: 'none',
                            borderRadius: '5px',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            cursor: otpLoading || !formData.phone ? 'not-allowed' : 'pointer',
                            marginTop: '10px'
                          }}
                        >
                          {otpLoading ? 'Sending OTP...' : 'Verify WhatsApp Number'}
                        </button>
                      )}

                      {/* OTP Input */}
                      {whatsappVerificationEnabled && otpSent && !otpVerified && (
                        <div style={{ marginTop: '10px' }}>
                          <input
                            type="text"
                            placeholder="Enter 6-digit OTP"
                            value={otpCode}
                            onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            maxLength="6"
                            style={{
                              width: '100%',
                              padding: '12px',
                              fontSize: '14px',
                              border: '1px solid #ddd',
                              borderRadius: '5px',
                              marginBottom: '10px',
                              textAlign: 'center',
                              letterSpacing: '5px',
                              fontSize: '18px',
                              fontWeight: 'bold'
                            }}
                          />
                          <div style={{ display: 'flex', gap: '10px' }}>
                            <button
                              type="button"
                              onClick={handleVerifyOTP}
                              disabled={otpLoading || otpCode.length !== 6}
                              style={{
                                flex: 1,
                                padding: '10px',
                                backgroundColor: otpLoading || otpCode.length !== 6 ? '#95a5a6' : '#27ae60',
                                color: 'white',
                                border: 'none',
                                borderRadius: '5px',
                                fontSize: '14px',
                                fontWeight: 'bold',
                                cursor: otpLoading || otpCode.length !== 6 ? 'not-allowed' : 'pointer'
                              }}
                            >
                              {otpLoading ? 'Verifying...' : 'Verify OTP'}
                            </button>
                            <button
                              type="button"
                              onClick={handleSendOTP}
                              disabled={otpLoading}
                              style={{
                                flex: 1,
                                padding: '10px',
                                backgroundColor: '#f39c12',
                                color: 'white',
                                border: 'none',
                                borderRadius: '5px',
                                fontSize: '14px',
                                fontWeight: 'bold',
                                cursor: otpLoading ? 'not-allowed' : 'pointer'
                              }}
                            >
                              Resend OTP
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="form-value">
                      {user.phone || 'Not provided'}
                      {user.whatsappVerified && user.phone && (
                        <span style={{
                          marginLeft: '10px',
                          color: '#27ae60',
                          fontSize: '16px',
                          fontWeight: 'bold'
                        }} title="WhatsApp Verified">
                          ✓
                        </span>
                      )}
                    </p>
                  )}
                </div>

                <div className="form-group">
                  <label>Organization</label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="organization"
                      value={formData.organization}
                      onChange={handleInputChange}
                    />
                  ) : (
                    <p className="form-value">{user.organization || 'Not provided'}</p>
                  )}
                </div>

                <div className="form-group full-width">
                  <label>Designation</label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="designation"
                      value={formData.designation}
                      onChange={handleInputChange}
                    />
                  ) : (
                    <p className="form-value">{user.designation || 'Not provided'}</p>
                  )}
                </div>
              </div>

              {isEditing && (
                <div className="form-actions">
                  <button type="button" className="btn-cancel" onClick={handleCancelEdit}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-save" disabled={saving}>
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              )}
            </form>
          </div>
        </div>

        {/* Sidebar */}
        <div className="profile-sidebar">
          {/* Subscription Summary Card */}
          {subscription && (
            <div className="sidebar-card">
              <h3>
                <i className="fas fa-crown"></i>
                Current Plan
              </h3>
              <div className="plan-info">
                <h4>{subscription.packageName}</h4>
                <p className="billing-cycle">{subscription.billingCycle} billing</p>
                <div className="plan-price">
                  ₹{subscription.price}
                  <span>/{subscription.billingCycle === 'monthly' ? 'month' : 'year'}</span>
                </div>
                <div className="plan-dates">
                  <p>
                    <i className="fas fa-calendar-check"></i>
                    Started: {formatDate(subscription.startDate)}
                  </p>
                  <p>
                    <i className="fas fa-calendar-times"></i>
                    {subscription.autoRenew ? 'Renews' : 'Ends'}: {formatDate(subscription.endDate)}
                  </p>
                </div>
                {/* Manage Subscription button hidden */}
              </div>
            </div>
          )}

          {/* Usage Statistics Card */}
          {usage && usage.submissionsLimit > 0 && (
            <div className="sidebar-card">
              <h3>
                <i className="fas fa-chart-bar"></i>
                Usage Statistics
              </h3>
              <div className="usage-info">
                <div className="usage-stats">
                  <div className="stat-item">
                    <span className="stat-value">{usage.submissionsUsed || 0}</span>
                    <span className="stat-label">Used</span>
                  </div>
                  <div className="stat-divider">/</div>
                  <div className="stat-item">
                    <span className="stat-value">{usage.submissionsLimit}</span>
                    <span className="stat-label">Total</span>
                  </div>
                </div>
                <div className="usage-bar">
                  <div
                    className="usage-progress"
                    style={{
                      width: `${((usage.submissionsUsed || 0) / usage.submissionsLimit) * 100}%`
                    }}
                  ></div>
                </div>
                <p className="usage-remaining">
                  {usage.submissionsLimit - (usage.submissionsUsed || 0)} submissions remaining
                </p>
              </div>
            </div>
          )}

          {/* Quick Actions hidden */}
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
